import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  IconButton, 
  Typography, 
  Card,
  CardMedia,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { 
  IconX, 
  IconDragDrop,
  IconFileSize,
  IconPlayerPlay,
  IconPlayerPause
} from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';

const MediaPreviewItem = ({ 
  file, 
  index, 
  onRemove, 
  onReorder,
  draggable = true,
  uploadProgress = null // 업로드 진행률 (0-100)
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [imageError, setImageError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(() => {
    // 새로운 파일 wrapper 구조 지원
    if (file.url) return file.url; // 업로드된 파일의 URL
    if (file.preview) return file.preview;
    if (file.file && file.file instanceof File) {
      return URL.createObjectURL(file.file);
    }
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return '';
  });

  // 파일 크기를 읽기 쉬운 형태로 변환
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 비디오 시간을 포맷팅
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 비디오 재생/일시정지 토글
  const toggleVideoPlay = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // 비디오 메타데이터 로드
  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
    }
  };

  // 파일 타입 확인 (wrapper 구조 지원)
  const fileType = file.file ? file.file.type : file.type;
  const isVideo = fileType && fileType.startsWith('video/');
  const isUploading = uploadProgress !== null && uploadProgress < 100;

  return (
    <Card
      sx={{
        position: 'relative',
        aspectRatio: '1',
        height: '120px', // 고정 높이로 크기 제한
        maxHeight: '120px',
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        cursor: draggable ? 'move' : 'default',
        border: `1px solid ${theme.palette.grey[200]}`,
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
      draggable={draggable && !isUploading}
      onDragStart={() => onReorder?.start?.(index)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onReorder?.end?.(index);
      }}
    >
      {/* 미디어 미리보기 */}
      {isVideo ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: theme.palette.grey[900]
          }}
        >
          <video
            ref={videoRef}
            src={previewUrl}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            muted
            preload="metadata"
            onError={() => setImageError(true)}
            onLoadedMetadata={handleVideoLoadedMetadata}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          
          {/* 비디오 컨트롤 오버레이 */}
          <Box
            onClick={toggleVideoPlay}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              opacity: isPlaying ? 0 : 1,
              transition: 'opacity 0.3s',
              cursor: 'pointer',
              '&:hover': {
                opacity: 1
              }
            }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.1)'
                }
              }}
            >
              {isPlaying ? <IconPlayerPause size={24} /> : <IconPlayerPlay size={24} />}
            </Box>
          </Box>

          {/* 비디오 시간 정보 */}
          {videoDuration && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: 1,
                fontSize: '0.75rem'
              }}
            >
              {formatDuration(videoDuration)}
            </Box>
          )}
        </Box>
      ) : (
        <CardMedia
          component="img"
          image={previewUrl}
          alt={file.name || '미디어 파일'}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={() => setImageError(true)}
        />
      )}

      {/* 업로드 진행률 오버레이 */}
      {isUploading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}
        >
          <CircularProgress 
            variant="determinate" 
            value={uploadProgress} 
            size={40}
            sx={{ color: 'white', mb: 1 }}
          />
          <Typography variant="caption">
            업로드 중... {Math.round(uploadProgress)}%
          </Typography>
        </Box>
      )}

      {/* 파일 정보 오버레이 */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.6))',
          color: 'white',
          padding: theme.spacing(1),
          opacity: 0,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 1 }
        }}
      >
        <Typography variant="caption" noWrap>
          {file.name || '알 수 없는 파일'}
        </Typography>
        <Typography variant="caption" display="block">
          {formatFileSize(file.size)}
        </Typography>
      </Box>

      {/* 삭제 버튼 */}
      <Tooltip title={t('common.delete')}>
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          disabled={isUploading}
          sx={{
            position: 'absolute',
            top: theme.spacing(0.5),
            right: theme.spacing(0.5),
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.9)'
            },
            width: 24,
            height: 24
          }}
        >
          <IconX size={16} />
        </IconButton>
      </Tooltip>

      {/* 드래그 핸들 */}
      {draggable && !isUploading && (
        <Box
          sx={{
            position: 'absolute',
            top: theme.spacing(0.5),
            left: theme.spacing(0.5),
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: theme.spacing(0.5),
            padding: theme.spacing(0.25),
            opacity: 0,
            transition: 'opacity 0.2s',
            '.MuiCard-root:hover &': { opacity: 1 }
          }}
        >
          <IconDragDrop size={16} color="white" />
        </Box>
      )}
    </Card>
  );
};

export default MediaPreviewItem;